import { Currency } from "@redotech/money/currencies";
import { getCurrencyFormatter } from "@redotech/money/util";
import { Subtotal } from "@redotech/redo-customer-portal-shared/types";
import { useTranslation } from "@redotech/redo-i18n";
import { Provider } from "@redotech/redo-model/order";
import { UpsellBillingTrigger } from "@redotech/redo-model/return";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { filterTruthy } from "@redotech/util/array";
import { memo } from "react";
import { DiscountInputWithButton } from "./discount-input-with-button";
import { IndentedTotals } from "./review/indented-totals";
import * as summaryCss from "./summary-card.module.css";

export const SummaryCard = memo(function SummaryCard({
  labelDeducted,
  shippingFee,
  totalShippingFee,
  pickupFee,
  newOrderValue,
  currency,
  totalPaymentOwed,
  totalRefund,
  totalStoreCredit,
  totalLoyaltyPointsDeducted,
  totalLoyaltyPointsRefunded,
  feesHeader,
  feesOrderHeader,
  adjustmentValue,
  adjustmentValueString,
  returnValue,
  newOrderTaxes,
  newOrderDiscountSubtotals,
  newVariantExchangeItemSubtotals,
  newOrderItemsSubtotals,
  productSubtotals,
  taxSubtotals,
  pointsDeductedFromRefundSubtotal,
  submitted,
  isMerchant,
  shippingFeeDeducted,
  provider,
  upsellBillingTrigger,
  exchangeShippingFee,
  exchangeShippingName,
  upsellFeeWaived,
  customerDiscountInputs,
  preDiscountShopNowBonus,
  combinedUnusedUseItOrLoseItBonuses,
  isConvenienceFee = false,
  chargesExchangeShippingViaStripe = true,
  hideItemCosts = false,
  isWarranty = false,
  dueLaterSubtext,
}: {
  feesHeader: string;
  feesOrderHeader: string;
  labelDeducted: boolean;
  shippingFee: number;
  totalShippingFee: number;
  pickupFee?: number;
  newOrderValue: number;
  currency: Currency;
  totalPaymentOwed: number;
  totalRefund: number;
  totalStoreCredit: number;
  adjustmentValue?: number;
  adjustmentValueString?: string;
  returnValue: number;
  newOrderDiscountSubtotals?: Subtotal[];
  newOrderTaxes: Subtotal[];
  newVariantExchangeItemSubtotals: Subtotal[];
  newOrderItemsSubtotals: Subtotal[];
  productSubtotals: Subtotal[];
  taxSubtotals: Subtotal[];
  pointsDeductedFromRefundSubtotal?: Subtotal;
  totalLoyaltyPointsDeducted?: number;
  totalLoyaltyPointsRefunded?: number;
  submitted: boolean;
  isMerchant?: boolean;
  shippingFeeDeducted?: number;
  provider?: Provider;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
  exchangeShippingFee?: number;
  exchangeShippingName?: string;
  upsellFeeWaived?: boolean;
  /**
   * Whether the customer-selected exchange shipping fee is collected via
   * Stripe at submit time. When false, the fee was already folded into the
   * `totalPaymentOwed` / "Due later" amount by the returns calculator;
   * we just split it back out for the breakdown sections. Defaults to true
   * (legacy always-Stripe display) for callers that haven't been updated.
   */
  chargesExchangeShippingViaStripe?: boolean;
  customerDiscountInputs?: {
    texts: { discountInputLabel: string; discountInputDescription: string };
    inputs: {
      enabled: boolean;
      discountCodes: string[];
      onChangeDiscountCodes: (discountCodes: string[]) => void;
      newDiscountCode: string;
      onChangeNewDiscountCode: (newDiscountCode: string) => void;
    };
  };
  preDiscountShopNowBonus?: number;
  combinedUnusedUseItOrLoseItBonuses?: number;
  /** When true, `ShippingFees` renders "Convenience fee" instead of "Return
   * shipping". Set by the caller based on the return's shipment method. */
  isConvenienceFee?: boolean;
  /** When true, hides item and draft-order amounts (return value breakdown,
   * new-order/draft totals, adjustments, refund/credit/payment-owed, and
   * loyalty points) while still showing return shipping costs. Used by the
   * warranty review page's "Hide warranty item costs" setting. */
  hideItemCosts?: boolean;
  isWarranty?: boolean;
  /** Merchant-customized copy for the non-immediate "Due later" amount. When
   * set, replaces the built-in invoiced-later explanation. A `%price%` token
   * is substituted with the formatted amount owed. */
  dueLaterSubtext?: string;
}) {
  const { t, i18n } = useTranslation();
  const currencyFormatter = getCurrencyFormatter(
    currency,
    undefined,
    i18n.language,
  );

  // Determine if payment is due immediately based on upsellBillingTrigger
  const isPaymentImmediate =
    upsellBillingTrigger === UpsellBillingTrigger.RETURN_CREATED;

  // When the exchange shipping fee is billed via the exchange draft order
  // (rather than via Stripe at submit), the calculator already folded it
  // into `totalPaymentOwed`. Split it back out for the visual breakdown:
  // pull it out of the "Due now" shipping section and surface it under the
  // draft totals.
  const draftBilledExchangeShippingFee =
    !chargesExchangeShippingViaStripe && exchangeShippingFee
      ? exchangeShippingFee
      : 0;
  const stripeShippingFee = totalShippingFee - draftBilledExchangeShippingFee;
  const stripeExchangeShippingFee = chargesExchangeShippingViaStripe
    ? exchangeShippingFee
    : undefined;

  // Only subtract stripeShippingFee from totalPaymentOwed when we actually
  // render a separate "Due now: shipping" section below (gated on the same
  // `!labelDeducted` condition). When the label was deducted (e.g. routed to
  // the exchange bucket), `totalPaymentOwed` already includes the shipping
  // fee via the RAC's `outstandingBalanceWithExchangeShippingDeduction` —
  // subtracting it here would under-display "Due later".
  let owedMinusShipping = totalPaymentOwed;
  if (stripeShippingFee && !labelDeducted) {
    owedMinusShipping = totalPaymentOwed - stripeShippingFee;
  }

  const isUpsell = owedMinusShipping > 0;
  const sign = isUpsell ? -1 : 1;

  // When the exchange shipping fee is being billed via the draft order, fold
  // it into the new-order breakdown so the customer sees it itemized as part
  // of the draft totals.
  const draftBilledExchangeShippingSubtotals: Subtotal[] =
    draftBilledExchangeShippingFee > 0
      ? [
          {
            title: exchangeShippingName
              ? t("Shipping ({{exchangeShippingName}})", {
                  exchangeShippingName,
                })
              : t("Shipping"),
            total: draftBilledExchangeShippingFee,
          },
        ]
      : [];
  const newOrderTotalWithDraftShipping =
    newOrderValue + draftBilledExchangeShippingFee;

  // When item costs are hidden (warranty "Hide warranty item costs"), the only
  // thing the card can still show is return shipping. If there's no shipping
  // cost to surface, there's no outstanding value — hide the card entirely.
  const deductedShippingFee = shippingFeeDeducted ?? shippingFee;
  const showsReturnShipping = labelDeducted
    ? deductedShippingFee > 0 || !!pickupFee
    : stripeShippingFee > 0;
  if (hideItemCosts && !showsReturnShipping) {
    return null;
  }

  return (
    <Flex data-ui="summary-card" flexDirection="column" gap="xl">
      <Text as="h2" fontSize="lg" fontWeight="semibold">
        {t("Summary")}
      </Text>
      {!!combinedUnusedUseItOrLoseItBonuses &&
        combinedUnusedUseItOrLoseItBonuses > 0 && (
          <RedoAlert
            color="gray"
            showCloseButton={false}
            title={t("{{amount}} bonus remaining for exchange only", {
              amount: currencyFormatter.format(
                combinedUnusedUseItOrLoseItBonuses,
              ),
            })}
            uiSelector="bonus-remaining-alert"
          />
        )}
      {!hideItemCosts && newOrderValue > 0 && isUpsell && (
        <IndentedTotals
          currencyFormatter={currencyFormatter}
          subtotals={[
            ...newOrderItemsSubtotals,
            ...newVariantExchangeItemSubtotals,
            ...(newOrderDiscountSubtotals ?? []),
            ...newOrderTaxes,
            ...draftBilledExchangeShippingSubtotals,
          ]}
          title={feesOrderHeader}
          total={newOrderTotalWithDraftShipping}
        />
      )}
      {!hideItemCosts && (
        <IndentedTotals
          currencyFormatter={currencyFormatter}
          subtotals={filterTruthy([
            ...productSubtotals,
            ...taxSubtotals,
            pointsDeductedFromRefundSubtotal,
          ])}
          title={feesHeader}
          total={returnValue * sign}
        />
      )}
      {!hideItemCosts &&
      adjustmentValue &&
      adjustmentValueString &&
      adjustmentValue !== 0 ? (
        <Flex data-ui="adjustment-row" dir="row" justify="space-between">
          <Text fontSize="sm">{adjustmentValueString}</Text>
          <Text fontSize="sm" textColor="secondary">
            {currencyFormatter.format(adjustmentValue * sign)}
          </Text>
        </Flex>
      ) : null}
      {!hideItemCosts && newOrderValue > 0 && !isUpsell && (
        <IndentedTotals
          currencyFormatter={currencyFormatter}
          subtotals={[
            ...newOrderItemsSubtotals,
            ...newVariantExchangeItemSubtotals,
            ...(newOrderDiscountSubtotals ?? []),
            ...newOrderTaxes,
            ...draftBilledExchangeShippingSubtotals,
          ]}
          title={feesOrderHeader}
          total={newOrderTotalWithDraftShipping * -1}
        />
      )}
      {labelDeducted && (
        // Only show what was actually deducted from credit/refund — the
        // exchange shipping fee is never deducted (it's Stripe-billed or
        // folded into the draft order), so it doesn't belong here.
        // Rendered as a positive amount: the customer is still being
        // charged for shipping (via reduced refund/credit or the exchange
        // invoice), which matches the page's convention that positive
        // numbers are on-the-draft charges and negatives are return-value
        // credits.
        <ShippingFees
          currency={currency}
          exchangeShippingName={exchangeShippingName}
          isConvenienceFee={isConvenienceFee}
          pickupFee={pickupFee}
          shippingFee={shippingFeeDeducted ?? shippingFee}
        />
      )}
      {!hideItemCosts && <Divider />}
      {!hideItemCosts && owedMinusShipping > 0 && (
        <>
          <Flex data-ui="payment-owed" dir="row" justify="space-between">
            <Text fontSize="sm">
              {isPaymentImmediate
                ? submitted
                  ? t("Paid")
                  : t("Due now")
                : t("Due later")}
            </Text>
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {currencyFormatter.format(
                upsellFeeWaived ? 0 : owedMinusShipping,
              )}
            </Text>
          </Flex>
          {!(
            submitted &&
            upsellBillingTrigger === UpsellBillingTrigger.RETURN_CREATED
          ) &&
            !upsellFeeWaived && (
              <Text fontSize="sm" textColor="primary">
                {isPaymentImmediate
                  ? isMerchant
                    ? t(
                        "The customer will be charged {{amount}} immediately for their exchange",
                        { amount: currencyFormatter.format(owedMinusShipping) },
                      )
                    : t(
                        "You will be charged {{amount}} immediately for your exchange",
                        { amount: currencyFormatter.format(owedMinusShipping) },
                      )
                  : dueLaterSubtext
                    ? dueLaterSubtext.replaceAll(
                        "%price%",
                        currencyFormatter.format(owedMinusShipping),
                      )
                    : isMerchant
                      ? isWarranty
                        ? t(
                            "The customer will be sent an invoice for the remaining {{amount}} balance for their new items once the warranty has been handled",
                            {
                              amount:
                                currencyFormatter.format(owedMinusShipping),
                            },
                          )
                        : t(
                            "The customer will be sent an invoice for the remaining {{amount}} balance for their new items once the return has been handled",
                            {
                              amount:
                                currencyFormatter.format(owedMinusShipping),
                            },
                          )
                      : isWarranty
                        ? t(
                            "You will be sent an invoice for the remaining {{amount}} balance for your new items once your warranty has been handled",
                            {
                              amount:
                                currencyFormatter.format(owedMinusShipping),
                            },
                          )
                        : t(
                            "You will be sent an invoice for the remaining {{amount}} balance for your new items once your return has been handled",
                            {
                              amount:
                                currencyFormatter.format(owedMinusShipping),
                            },
                          )}
              </Text>
            )}
        </>
      )}
      {!hideItemCosts && totalStoreCredit > 0 && (
        <Flex data-ui="store-credit" dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            {t("Store credit")}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {currencyFormatter.format(totalStoreCredit)}
          </Text>
        </Flex>
      )}
      {!hideItemCosts &&
        !!preDiscountShopNowBonus &&
        preDiscountShopNowBonus > 0 && (
          <Flex
            align="center"
            data-ui="shop-now-bonus"
            dir="row"
            justify="space-between"
          >
            <Flex align="center" dir="row" gap="sm">
              <Text fontSize="sm" textColor="secondary">
                {t("Exchange bonus")}
              </Text>
            </Flex>
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {currencyFormatter.format(-preDiscountShopNowBonus)}
            </Text>
          </Flex>
        )}
      {!hideItemCosts && totalRefund > 0 && (
        <Flex data-ui="refund-amount" dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            {t("Refund amount")}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {currencyFormatter.format(totalRefund)}
          </Text>
        </Flex>
      )}
      {!hideItemCosts &&
        totalRefund <= 0 &&
        totalStoreCredit <= 0 &&
        owedMinusShipping <= 0 && (
          <Flex data-ui="subtotal" dir="row" justify="space-between">
            <Text fontSize="sm">{t("Subtotal")}</Text>
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {currencyFormatter.format(0)}
            </Text>
          </Flex>
        )}

      {stripeShippingFee > 0 && !labelDeducted && (
        <>
          {!hideItemCosts && <Divider classes={summaryCss.fullDivider} />}
          <Flex data-ui="shipping-section" flexDirection="column" gap="xl">
            <Text fontSize="sm" fontWeight="semibold">
              {t("Shipping")}
            </Text>
            <ShippingFees
              currency={currency}
              exchangeShippingFee={stripeExchangeShippingFee}
              exchangeShippingName={exchangeShippingName}
              isConvenienceFee={isConvenienceFee}
              pickupFee={pickupFee}
              shippingFee={shippingFee}
            />
            <Divider />

            <Flex data-ui="shipping-due" dir="row" justify="space-between">
              <Text fontSize="sm">{submitted ? t("Paid") : t("Due now")}</Text>
              <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                {currencyFormatter.format(stripeShippingFee)}
              </Text>
            </Flex>
          </Flex>
        </>
      )}

      {!hideItemCosts && !!totalLoyaltyPointsDeducted && (
        <Flex
          data-ui="loyalty-points-deducted"
          dir="row"
          justify="space-between"
        >
          <Text fontSize="sm" textColor="secondary">
            {t("Loyalty Points Deducted")}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            -{totalLoyaltyPointsDeducted}
          </Text>
        </Flex>
      )}
      {!hideItemCosts && !!totalLoyaltyPointsRefunded && (
        <Flex
          data-ui="loyalty-points-refunded"
          dir="row"
          justify="space-between"
        >
          <Text fontSize="sm" textColor="secondary">
            {t("Loyalty Points Refunded")}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {totalLoyaltyPointsRefunded}
          </Text>
        </Flex>
      )}

      {!hideItemCosts && customerDiscountInputs && !isMerchant && (
        <DiscountInputWithButton
          description={customerDiscountInputs.texts.discountInputDescription}
          discountCodes={customerDiscountInputs.inputs.discountCodes}
          enabled={customerDiscountInputs.inputs.enabled}
          label={customerDiscountInputs.texts.discountInputLabel}
          newDiscountCode={customerDiscountInputs.inputs.newDiscountCode}
          onChangeDiscountCodes={
            customerDiscountInputs.inputs.onChangeDiscountCodes
          }
          onChangeNewDiscountCode={
            customerDiscountInputs.inputs.onChangeNewDiscountCode
          }
        />
      )}
    </Flex>
  );
});

export const ShippingFees = memo(function ShippingFees({
  shippingFee,
  pickupFee,
  currency,
  negative,
  exchangeShippingFee,
  exchangeShippingName,
  isConvenienceFee = false,
}: {
  shippingFee: number;
  currency: Currency;
  pickupFee?: number;
  negative?: boolean;
  exchangeShippingFee?: number;
  exchangeShippingName?: string;
  /** When true, renders "Convenience fee" instead of "Return shipping". */
  isConvenienceFee?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const currencyFormatter = getCurrencyFormatter(
    currency,
    undefined,
    i18n.language,
  );
  return (
    <>
      {shippingFee > 0 && (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            {isConvenienceFee ? t("Convenience fee") : t("Return shipping")}
          </Text>
          <Text fontSize="sm" textColor="secondary">
            {`${negative ? "-" : ""}${currencyFormatter.format(shippingFee)}`}
          </Text>
        </Flex>
      )}
      {pickupFee && (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            {t("Package pickup")}
          </Text>
          <Text fontSize="sm" textColor="secondary">
            {`${negative ? "-" : ""}${currencyFormatter.format(pickupFee)}`}
          </Text>
        </Flex>
      )}
      {exchangeShippingFee && exchangeShippingFee > 0 ? (
        <Flex dir="row" justify="space-between">
          <Text fontSize="sm" textColor="secondary">
            {t("New order shipping ({{exchangeShippingName}})", {
              exchangeShippingName,
            })}
          </Text>
          <Text fontSize="sm" textColor="secondary">
            {`${negative ? "-" : ""}${currencyFormatter.format(exchangeShippingFee)}`}
          </Text>
        </Flex>
      ) : null}
    </>
  );
});
